import { useState } from "react";
import { useRouter } from "next/navigation";
import {
  DocumentIcon,
  VideoCameraIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";

import { ModalData } from "@/app/(workspace)/_hooks/useModal";
import { useUploadFile } from "@/app/(workspace)/_hooks/useUploadFile";
import { useProjectModal } from "@/app/(workspace)/_providers/ModalProvider";
import { useSubscription } from "@/lib/firebase/hooks/useSubscription";
import {
  LodProfile,
  ProjectFormInput,
  WorldDensity,
} from "@/app/(workspace)/_types/project";
import { ModalInput } from "./ModalInput";
import { formatFileSize, generateFileName } from "@/utils/file";
import { pushToast } from "@/utils/toast";
import { SUBSCRIPTION_LIMITS, SubscriptionTier } from "@/lib/firebase/types/subscription";

const INPUT_BASE_STYLE =
  "w-full px-4 py-2.5 bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-sm text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 focus:border-transparent transition-colors";

// 각 옵션에 대한 최소 필요 plan 정의
const QUALITY_OPTIONS = [
  { id: "lite", name: "Lite", default: true, requiredTier: null },
  { id: "plus", name: "Plus", default: false, requiredTier: null },
  { id: "ultra", name: "Ultra", default: false, requiredTier: SubscriptionTier.PRO },
] as const;

const SCENE_SIZE_OPTIONS = [
  { id: "normal", name: "Normal", default: true, requiredTier: null },
  { id: "world", name: "World", default: false, requiredTier: SubscriptionTier.ULTRA },
] as const;

// World Density 옵션 (sceneSize가 "world"일 때만 표시)
const WORLD_DENSITY_OPTIONS = [
  { id: "low", name: "Low", default: true, recommended: true },
  { id: "medium", name: "Medium", default: false, recommended: false },
  { id: "high", name: "High", default: false, recommended: false },
  { id: "max", name: "Max", default: false, recommended: false },
] as const;

// LoD Profile 옵션 (Spark.js 2.0)
//   fast: 모든 플랜 (Default)
//   balanced: Pro+
//   max: Ultra+
const LOD_PROFILE_OPTIONS: Array<{
  id: LodProfile;
  name: string;
  requiredTier: SubscriptionTier | null;
}> = [
  { id: "fast", name: "Fast", requiredTier: null },
  { id: "balanced", name: "Balanced", requiredTier: SubscriptionTier.PRO },
  { id: "max", name: "Max", requiredTier: SubscriptionTier.ULTRA },
];

// plan tier 순서 (비교용)
const TIER_ORDER: Record<string, number> = {
  [SubscriptionTier.STANDARD]: 0,
  [SubscriptionTier.PRO]: 1,
  [SubscriptionTier.ULTRA]: 2,
  [SubscriptionTier.ENTERPRISE]: 3,
};

// plan tier 표시 이름
const TIER_DISPLAY_NAMES: Record<string, string> = {
  [SubscriptionTier.STANDARD]: "Standard",
  [SubscriptionTier.PRO]: "Pro",
  [SubscriptionTier.ULTRA]: "Ultra",
  [SubscriptionTier.ENTERPRISE]: "Enterprise",
};

interface CreateProjectModalProps {
  data: ModalData;
  onClose: () => void;
}

// 파일명에서 확장자를 제거하는 함수
const getFileNameWithoutExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex === -1) return fileName;
  return fileName.substring(0, lastDotIndex);
};

export const CreateProjectModal = ({
  data,
  onClose,
}: CreateProjectModalProps) => {
  const router = useRouter();
  const { open } = useProjectModal();
  const { upload } = useUploadFile();
  const { subscription, loading: subscriptionLoading } = useSubscription();

  // 파일명을 초기 title로 설정
  const initialTitle = data.selectedFile
    ? getFileNameWithoutExtension(data.selectedFile.name)
    : "";

  const [form, setForm] = useState<ProjectFormInput>({
    title: initialTitle,
    options: {
      quality: "lite",
      sceneSize: "normal",
      autoColorCorrection: false,
      worldDensity: "low", // sceneSize가 "world"일 때만 사용
      lodProfile: "fast", // 기본값: 모든 플랜에서 사용 가능
      useExtSplats: false, // 기본값: Off (Ultra+ 사용자가 명시적으로 켤 수 있음)
    },
    output: "sample_output", // FIXME: 임시값
  });
  const [errors, setErrors] = useState({
    title: "",
  });
  const [showDensityPopover, setShowDensityPopover] = useState(false);

  // 현재 사용자의 tier
  const currentTier = subscription?.tier || SubscriptionTier.STANDARD;

  // 특정 옵션이 현재 plan으로 사용 가능한지 확인
  const isOptionAvailable = (requiredTier: string | null): boolean => {
    if (!requiredTier) return true;
    return TIER_ORDER[currentTier] >= TIER_ORDER[requiredTier];
  };

  // Auto Color Correction 사용 가능 여부
  const isAutoColorAvailable = (): boolean => {
    if (!subscription) return false;
    const limits = SUBSCRIPTION_LIMITS[subscription.tier];
    return limits.autoColorCorrection;
  };

  // High Precision (ExtSplats) 사용 가능 여부 — Ultra+
  const isHighPrecisionAvailable = (): boolean => {
    if (!subscription) return false;
    const limits = SUBSCRIPTION_LIMITS[subscription.tier];
    return limits.highPrecision;
  };

  // Upgrade 모달 표시
  const showUpgradeModal = (requiredTier: string) => {
    const tierName = TIER_DISPLAY_NAMES[requiredTier] || requiredTier.toUpperCase();
    open(
      {
        type: "message",
        title: "Upgrade Required",
        size: "sm",
        message: `This feature requires ${tierName} plan or higher. Would you like to upgrade your plan?`,
        confirmText: "Upgrade",
        cancelText: "Cancel",
        onConfirm: () => {
          onClose();
          router.push("/subscription/upgrade");
        },
      },
      true
    );
  };

  const getValidationError = {
    title: (input: string): string | null => {
      if (input.trim() === "")
        return "Title is required. Please enter a title.";
      return null;
    },
  };

  // 프로젝트 한도 초과 시 업그레이드 모달 표시
  const showQuotaExceededModal = () => {
    const limits = SUBSCRIPTION_LIMITS[currentTier];
    const tierName = TIER_DISPLAY_NAMES[currentTier] || currentTier.toUpperCase();
    const nextTier = currentTier === SubscriptionTier.STANDARD
      ? SubscriptionTier.PRO
      : currentTier === SubscriptionTier.PRO
        ? SubscriptionTier.ULTRA
        : SubscriptionTier.ENTERPRISE;
    const nextTierName = TIER_DISPLAY_NAMES[nextTier] || nextTier.toUpperCase();

    open(
      {
        type: "message",
        title: "Project Limit Reached",
        size: "sm",
        message: `You have reached your monthly project limit (${limits.monthlyProjects} projects) on the ${tierName} plan. Upgrade to ${nextTierName} to create more projects.`,
        confirmText: "Upgrade",
        cancelText: "Cancel",
        onConfirm: () => {
          onClose();
          router.push("/subscription/upgrade");
        },
      },
      true
    );
  };

  // 파일 크기 제한 초과 시 업그레이드 모달 표시
  const showFileSizeExceededModal = (currentLimit: number) => {
    const tierName = TIER_DISPLAY_NAMES[currentTier] || currentTier.toUpperCase();
    const nextTier = currentTier === SubscriptionTier.STANDARD
      ? SubscriptionTier.PRO
      : currentTier === SubscriptionTier.PRO
        ? SubscriptionTier.ULTRA
        : SubscriptionTier.ENTERPRISE;
    const nextTierName = TIER_DISPLAY_NAMES[nextTier] || nextTier.toUpperCase();
    const nextLimits = SUBSCRIPTION_LIMITS[nextTier];
    const nextFileSize = nextLimits.maxFileSize === -1 ? "Unlimited" : `${nextLimits.maxFileSize}MB`;

    open(
      {
        type: "message",
        title: "File Size Limit Exceeded",
        size: "sm",
        message: `Your file exceeds the maximum size limit (${currentLimit}MB) for the ${tierName} plan. Upgrade to ${nextTierName} to upload files up to ${nextFileSize}.`,
        confirmText: "Upgrade",
        cancelText: "Cancel",
        onConfirm: () => {
          onClose();
          router.push("/subscription/upgrade");
        },
      },
      true
    );
  };

  const handleUpload = async () => {
    const newErrors = {
      title: getValidationError.title(form.title) || "",
    };

    setErrors(newErrors);

    if (newErrors.title) {
      return;
    }

    if (!data.selectedFile) return;

    // Generate uuid before upload to use as consistent uploadKey
    const uuid = generateFileName(data.selectedFile.name);

    // firebase storage upload
    try {
      showProgressModal(uuid);
      await upload(data.selectedFile, form, uuid);

      pushToast({
        type: "success",
        message: "Project created successfully.",
      });

      await new Promise((resolve) => setTimeout(resolve, 100));
      window.location.reload();
    } catch (err) {
      console.error("upload failed", err);

      const errorMessage = err instanceof Error ? err.message : String(err);

      // 파일 크기 제한 초과 에러인지 확인
      if (errorMessage.includes("파일 크기") || errorMessage.includes("file size")) {
        const limits = SUBSCRIPTION_LIMITS[currentTier];
        showFileSizeExceededModal(limits.maxFileSize);
        return;
      }

      // 프로젝트 한도 초과 에러인지 확인
      if (errorMessage.includes("한도") || errorMessage.includes("limit")) {
        showQuotaExceededModal();
        return;
      }

      pushToast({
        type: "error",
        message: "Failed to create project. Please try again.",
      });
    }
  };

  const showProgressModal = (uuid: string) => {
    open(
      {
        type: "progress",
        title: "SplatX",
        size: "md",
        message: `Please wait`,
        confirmText: "Continue",
        uploadKey: uuid,
      },
      true // replace current modal
    );
  };

  const isVideoFile = (fileName: string): boolean => {
    const videoExtensions = ['.mp4', '.mov', '.webm', '.avi'];
    return videoExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
  };

  // Plan Tag 컴포넌트 (플랜별 색상 통일)
  const getPlanTagStyle = (tier: string) => {
    switch (tier) {
      case SubscriptionTier.STANDARD:
        return "bg-blue-100 text-blue-700 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-1 dark:ring-inset dark:ring-blue-400/20";
      case SubscriptionTier.PRO:
        return "bg-emerald-100 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-400 dark:ring-1 dark:ring-inset dark:ring-emerald-400/20";
      case SubscriptionTier.ULTRA:
        return "bg-rose-100 text-rose-700 dark:bg-rose-400/10 dark:text-rose-400 dark:ring-1 dark:ring-inset dark:ring-rose-400/20";
      case SubscriptionTier.ENTERPRISE:
        return "bg-amber-100 text-amber-700 dark:bg-amber-400/10 dark:text-amber-400 dark:ring-1 dark:ring-inset dark:ring-amber-400/20";
      default:
        return "bg-zinc-100 text-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-400";
    }
  };

  const PlanTag = ({ tier }: { tier: string }) => {
    const tierName = TIER_DISPLAY_NAMES[tier] || tier.toUpperCase();
    return (
      <span className={`ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${getPlanTagStyle(tier)}`}>
        {tierName}+
      </span>
    );
  };

  // Quality 옵션 선택 핸들러
  const handleQualitySelect = (option: typeof QUALITY_OPTIONS[number]) => {
    if (option.requiredTier && !isOptionAvailable(option.requiredTier)) {
      showUpgradeModal(option.requiredTier);
      return;
    }
    setForm({
      ...form,
      options: {
        ...form.options,
        quality: option.id as "lite" | "plus" | "ultra",
      },
    });
  };

  // Scene Size 옵션 선택 핸들러
  const handleSceneSizeSelect = (option: typeof SCENE_SIZE_OPTIONS[number]) => {
    if (option.requiredTier && !isOptionAvailable(option.requiredTier)) {
      showUpgradeModal(option.requiredTier);
      return;
    }
    const newSceneSize = option.id as "normal" | "world";
    setForm({
      ...form,
      options: {
        ...form.options,
        sceneSize: newSceneSize,
        // sceneSize가 world가 아니면 worldDensity를 undefined로 설정
        worldDensity: newSceneSize === "world" ? (form.options.worldDensity || "low") : undefined,
      },
    });
  };

  // World Density 옵션 선택 핸들러
  const handleWorldDensitySelect = (option: typeof WORLD_DENSITY_OPTIONS[number]) => {
    setForm({
      ...form,
      options: {
        ...form.options,
        worldDensity: option.id as WorldDensity,
      },
    });
  };

  // Auto Color Correction 토글 핸들러
  const handleAutoColorToggle = () => {
    if (!isAutoColorAvailable() && !form.options.autoColorCorrection) {
      showUpgradeModal(SubscriptionTier.PRO);
      return;
    }
    setForm({
      ...form,
      options: {
        ...form.options,
        autoColorCorrection: !form.options.autoColorCorrection,
      },
    });
  };

  // LoD Profile 옵션 선택 핸들러 (플랜 차등)
  const handleLodProfileSelect = (option: typeof LOD_PROFILE_OPTIONS[number]) => {
    if (option.requiredTier && !isOptionAvailable(option.requiredTier)) {
      showUpgradeModal(option.requiredTier);
      return;
    }
    setForm({
      ...form,
      options: { ...form.options, lodProfile: option.id },
    });
  };

  // High Precision (ExtSplats) 토글 핸들러 — Ultra+ 차등
  const handleHighPrecisionToggle = () => {
    if (!isHighPrecisionAvailable() && !form.options.useExtSplats) {
      showUpgradeModal(SubscriptionTier.ULTRA);
      return;
    }
    setForm({
      ...form,
      options: {
        ...form.options,
        useExtSplats: !(form.options.useExtSplats ?? false),
      },
    });
  };

  return (
    <div className="bg-white dark:bg-zinc-900 select-none">
      <div className="flex flex-col md:flex-row gap-6 p-6 sm:p-8">
        {/* Left Column - File Preview */}
        <div className="flex flex-col md:w-80 flex-shrink-0">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-3">
            File Preview
          </h3>
          <div className="flex-1 flex flex-col items-center justify-center p-8 bg-zinc-50 dark:bg-zinc-800/50 rounded-xl ring-1 ring-zinc-900/5 dark:ring-white/10">
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 bg-white dark:bg-zinc-900 rounded-2xl ring-1 ring-zinc-900/5 dark:ring-white/10">
                {data.selectedFile && isVideoFile(data.selectedFile.name) ? (
                  <VideoCameraIcon className="w-16 h-16 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <DocumentIcon className="w-16 h-16 text-emerald-600 dark:text-emerald-400" />
                )}
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm font-semibold text-zinc-900 dark:text-white break-all px-4">
                  {data.selectedFile?.name || "No file selected"}
                </p>
                {data.selectedFile && (
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">
                    {formatFileSize(data.selectedFile.size)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Project Information */}
        <div className="flex flex-col flex-1">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-3">
            Project Information
          </h3>
          <div className="flex-1 space-y-4">
            {/* Title Input */}
            <ModalInput title="Title" error={errors.title}>
              <input
                type="text"
                value={form.title}
                onChange={(e) => {
                  setForm({ ...form, title: e.target.value });
                  if (errors.title) {
                    setErrors({ ...errors, title: "" });
                  }
                }}
                placeholder="Enter project title"
                className={INPUT_BASE_STYLE}
              />
            </ModalInput>

            {/* Quality Input */}
            <ModalInput title="Quality">
              <div className="grid grid-cols-3 gap-3">
                {QUALITY_OPTIONS.map((option) => {
                  const isLocked = option.requiredTier && !isOptionAvailable(option.requiredTier);
                  const isSelected = form.options.quality === option.id;

                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleQualitySelect(option)}
                      className={`
                        group relative flex flex-col items-center justify-center rounded-lg border p-3 transition-colors
                        ${isSelected
                          ? "border-emerald-600 bg-emerald-600 dark:border-emerald-500 dark:bg-emerald-500"
                          : isLocked
                            ? "border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50 cursor-pointer hover:border-violet-400 dark:hover:border-violet-500"
                            : "border-zinc-300 bg-white dark:border-zinc-700 dark:bg-zinc-800 cursor-pointer hover:border-emerald-500 dark:hover:border-emerald-400"
                        }
                      `}
                    >
                      <div className="flex items-center">
                        <span className={`
                          text-sm font-semibold
                          ${isSelected
                            ? "text-white"
                            : isLocked
                              ? "text-zinc-400 dark:text-zinc-500"
                              : "text-zinc-900 dark:text-white"
                          }
                        `}>
                          {option.name}
                        </span>
                        {option.requiredTier && <PlanTag tier={option.requiredTier} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </ModalInput>

            {/* Scene Size Input */}
            <ModalInput title="Scene Size">
              <div className="grid grid-cols-2 gap-3">
                {SCENE_SIZE_OPTIONS.map((option) => {
                  const isLocked = option.requiredTier && !isOptionAvailable(option.requiredTier);
                  const isSelected = form.options.sceneSize === option.id;

                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleSceneSizeSelect(option)}
                      className={`
                        group relative flex flex-col items-center justify-center rounded-lg border p-3 transition-colors
                        ${isSelected
                          ? "border-emerald-600 bg-emerald-600 dark:border-emerald-500 dark:bg-emerald-500"
                          : isLocked
                            ? "border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50 cursor-pointer hover:border-violet-400 dark:hover:border-violet-500"
                            : "border-zinc-300 bg-white dark:border-zinc-700 dark:bg-zinc-800 cursor-pointer hover:border-emerald-500 dark:hover:border-emerald-400"
                        }
                      `}
                    >
                      <div className="flex items-center">
                        <span className={`
                          text-sm font-semibold
                          ${isSelected
                            ? "text-white"
                            : isLocked
                              ? "text-zinc-400 dark:text-zinc-500"
                              : "text-zinc-900 dark:text-white"
                          }
                        `}>
                          {option.name}
                        </span>
                        {option.requiredTier && <PlanTag tier={option.requiredTier} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </ModalInput>

            {/* World Density Input - sceneSize가 "world"일 때만 표시 */}
            {form.options.sceneSize === "world" && (
              <ModalInput
                title={
                  <div className="flex items-center gap-1.5">
                    <span>World Density</span>
                    <div
                      className="relative group"
                      onMouseEnter={() => setShowDensityPopover(true)}
                      onMouseLeave={() => setShowDensityPopover(false)}
                      onTouchStart={() => setShowDensityPopover(!showDensityPopover)}
                    >
                      <QuestionMarkCircleIcon className="w-4 h-4 text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300 cursor-help transition-colors" />
                      {showDensityPopover && (
                        <div className="absolute left-1/2 -translate-x-1/2 top-6 z-50 w-64 p-3 bg-white dark:bg-zinc-800 rounded-lg shadow-lg ring-1 ring-zinc-900/10 dark:ring-white/10 sm:left-0 sm:translate-x-0">
                          <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed">
                            World Density affects the number of splats generated. Higher density produces more detailed results but increases processing time and file size.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                }
              >
                <div className="grid grid-cols-4 gap-2">
                  {WORLD_DENSITY_OPTIONS.map((option) => {
                    const isSelected = form.options.worldDensity === option.id;

                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleWorldDensitySelect(option)}
                        className={`
                          group relative flex flex-col items-center justify-center rounded-lg border p-2.5 transition-colors
                          ${isSelected
                            ? "border-violet-600 bg-violet-600 dark:border-violet-500 dark:bg-violet-500"
                            : option.recommended
                              ? "border-violet-300 bg-violet-50 dark:border-violet-600 dark:bg-violet-900/20 cursor-pointer hover:border-violet-500 dark:hover:border-violet-400 ring-1 ring-violet-200 dark:ring-violet-700"
                              : "border-zinc-300 bg-white dark:border-zinc-700 dark:bg-zinc-800 cursor-pointer hover:border-violet-500 dark:hover:border-violet-400"
                          }
                        `}
                      >
                        {/* Recommended 뱃지 */}
                        {option.recommended && !isSelected && (
                          <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-violet-500 text-white rounded-full whitespace-nowrap">
                            Recommended
                          </span>
                        )}
                        <span className={`
                          text-sm font-semibold
                          ${isSelected
                            ? "text-white"
                            : option.recommended
                              ? "text-violet-700 dark:text-violet-300"
                              : "text-zinc-900 dark:text-white"
                          }
                        `}>
                          {option.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </ModalInput>
            )}

            {/* Auto Color Correction Input */}
            <ModalInput title="Auto Color Correction">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.options.autoColorCorrection}
                  aria-label="Toggle auto color correction"
                  onClick={handleAutoColorToggle}
                  className={`
                    relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2
                    ${!isAutoColorAvailable() ? "cursor-pointer" : "cursor-pointer"}
                    ${form.options.autoColorCorrection
                      ? "bg-emerald-600 dark:bg-emerald-500"
                      : isAutoColorAvailable()
                        ? "bg-zinc-200 dark:bg-zinc-700"
                        : "bg-zinc-200 dark:bg-zinc-700"
                    }
                  `}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${form.options.autoColorCorrection
                      ? "translate-x-5"
                      : "translate-x-0"
                      }`}
                  />
                </button>
                <PlanTag tier={SubscriptionTier.PRO} />
              </div>
            </ModalInput>

            {/* LoD Profile (플랜 차등: Fast 모든 플랜, Balanced Pro+, Max Ultra+) */}
            <ModalInput title="LoD Profile">
              <div className="grid grid-cols-3 gap-3">
                {LOD_PROFILE_OPTIONS.map((option) => {
                  const isLocked = option.requiredTier && !isOptionAvailable(option.requiredTier);
                  const isSelected = form.options.lodProfile === option.id;

                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleLodProfileSelect(option)}
                      className={`
                        group relative flex flex-col items-center justify-center rounded-lg border p-3 transition-colors
                        ${isSelected
                          ? "border-emerald-600 bg-emerald-600 dark:border-emerald-500 dark:bg-emerald-500"
                          : isLocked
                            ? "border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50 cursor-pointer hover:border-violet-400 dark:hover:border-violet-500"
                            : "border-zinc-300 bg-white dark:border-zinc-700 dark:bg-zinc-800 cursor-pointer hover:border-emerald-500 dark:hover:border-emerald-400"
                        }
                      `}
                    >
                      <div className="flex items-center">
                        <span className={`
                          text-sm font-semibold
                          ${isSelected
                            ? "text-white"
                            : isLocked
                              ? "text-zinc-400 dark:text-zinc-500"
                              : "text-zinc-900 dark:text-white"
                          }
                        `}>
                          {option.name}
                        </span>
                        {option.requiredTier && <PlanTag tier={option.requiredTier} />}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-[11px] text-zinc-500 dark:text-zinc-400 leading-tight">
                Higher profiles produce better LoD quality but take longer to build.
              </p>
            </ModalInput>

            {/* High Precision (ExtSplats float32 좌표) — Ultra+ 전용 */}
            <ModalInput title="High Precision">
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.options.useExtSplats ?? false}
                  aria-label="Toggle high precision"
                  onClick={handleHighPrecisionToggle}
                  className={`
                    relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 cursor-pointer
                    ${form.options.useExtSplats ?? false
                      ? "bg-emerald-600 dark:bg-emerald-500"
                      : "bg-zinc-200 dark:bg-zinc-700"
                    }
                  `}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${form.options.useExtSplats ?? false
                      ? "translate-x-5"
                      : "translate-x-0"
                      }`}
                  />
                </button>
                <PlanTag tier={SubscriptionTier.ULTRA} />
              </div>
              <p className="mt-2 text-[11px] text-zinc-500 dark:text-zinc-400 leading-tight">
                Reduces banding artifacts in large scenes by using float32 coordinate precision. Slightly higher memory usage.
              </p>
            </ModalInput>
          </div>
        </div>
      </div>

      {/* Bottom Buttons */}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/30">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleUpload}
          disabled={subscriptionLoading}
          className="px-6 py-2 text-sm font-semibold text-white bg-emerald-600 dark:bg-emerald-500 rounded-lg hover:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Upload
        </button>
      </div>
    </div>
  );
};
